import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  recordingConsentPeerPrompt,
  recordingConsentRequestDraft,
  recordingConsentSkippedMessage,
} from '@shared/calls/recording-consent'
import { env } from '../../../config/env'
import { useAuth } from '../../../contexts/auth-context'
import { getOrCreateDirectConversation, resolveSelfUserId } from '../../../services/chat-service'
import {
  clearRecordingConsentPeerNotice,
  useRecordingConsentPeerNotice,
  type UnconsentedCallPerson,
} from '../../../stores/recording-consent-peer-store'
import { saveConversationDraftPrefill } from '../../../utils/conversation-drafts'

/**
 * Caller-side notice when someone hasn't accepted call recording, so the call
 * was not placed (1:1) or they weren't called (group). "Send message" opens
 * the 1:1 thread with a prefilled draft the user can edit — it is NEVER sent
 * automatically. Cancel just closes it.
 */
export function RecordingConsentPeerDialog() {
  const notice = useRecordingConsentPeerNotice()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const primaryRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    setOpeningId(null)
    setError(null)
  }, [notice])

  useEffect(() => {
    if (!notice) return
    primaryRef.current?.focus()
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearRecordingConsentPeerNotice()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [notice])

  if (!notice) return null

  const names = notice.people.map((p) => p.firstName)
  const single = notice.people.length === 1
  const title = notice.kind === 'peer' ? "Can't call yet" : 'Not everyone was called'
  const body =
    notice.kind === 'peer'
      ? recordingConsentPeerPrompt(names[0])
      : recordingConsentSkippedMessage(names)

  const handleSend = async (person: UnconsentedCallPerson) => {
    if (openingId) return
    const actorId = resolveSelfUserId(user?.userId)
    if (!actorId) {
      clearRecordingConsentPeerNotice()
      navigate('/messages/direct')
      return
    }
    setOpeningId(person.userId)
    setError(null)
    try {
      const thread = await getOrCreateDirectConversation(person.userId, actorId)
      // A draft only: the composer opens with this text, ready to edit.
      // Don't wipe unsent text the user already had in this thread.
      // The link opens the exact screen (the app intercepts its own URLs).
      saveConversationDraftPrefill(
        thread.id,
        recordingConsentRequestDraft(person.firstName, { appUrl: env.publicAppUrl }),
      )
      clearRecordingConsentPeerNotice()
      navigate(`/messages/direct?thread=${encodeURIComponent(thread.id)}`)
    } catch {
      setOpeningId(null)
      setError('Could not open the conversation. Please try again from Direct messages.')
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={() => clearRecordingConsentPeerNotice()}
    >
      <div
        className="animate-fade-slide-in w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recording-consent-peer-title"
        aria-describedby="recording-consent-peer-body"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pb-2 pt-6 text-center">
          <h2 id="recording-consent-peer-title" className="text-lg font-bold text-slate-900">
            {title}
          </h2>
          <p id="recording-consent-peer-body" className="mt-2 text-sm leading-relaxed text-slate-600">
            {body}
          </p>
        </div>

        <div className="space-y-2 p-4">
          {notice.people.map((person, index) => (
            <button
              key={person.userId}
              ref={index === 0 ? primaryRef : undefined}
              type="button"
              onClick={() => void handleSend(person)}
              disabled={openingId !== null}
              aria-label={single ? 'Send message' : `Send message to ${person.firstName || 'them'}`}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-60"
            >
              {openingId === person.userId
                ? 'Opening…'
                : single
                  ? 'Send message'
                  : `Send message to ${person.firstName || 'them'}`}
            </button>
          ))}

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={() => clearRecordingConsentPeerNotice()}
            className="w-full rounded-xl py-2.5 text-sm font-semibold text-slate-500 transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
